import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Calendar as CalIcon, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * Admin-only modal to edit address + contact + publication-date metadata of a request.
 * Onsuccessful save calls onSaved(updatedRequest) so the parent can refresh its state.
 */
export default function EditRequestDetailsModal({ open, onOpenChange, req, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [recalcTravel, setRecalcTravel] = useState(true);
    const [form, setForm] = useState({
        address_street: "",
        address_city: "",
        address_zip: "",
        email: "",
        phone: "",
        property_link: "",
        description: "",
        publication_date: null, // Date object | null
    });

    // Sync from req every time the dialog opens
    useEffect(() => {
        if (!open || !req) return;
        setForm({
            address_street: req.address_street || "",
            address_city: req.address_city || "",
            address_zip: req.address_zip || "",
            email: req.email || "",
            phone: req.phone || "",
            property_link: req.property_link || "",
            description: req.description || "",
            publication_date: req.publication_date ? new Date(`${req.publication_date}T00:00:00`) : null,
        });
        setRecalcTravel(true);
    }, [open, req]);

    // Show the recalc toggle when there's already a travel cost OR when the order
    // contains on-site items (in which case travel_cost = 0 means OSRM was likely
    // down at order time and the admin should be able to retry the calculation).
    const ON_SITE_PRODUCT_IDS = new Set(req?.items?.map((it) => it.product_id) || []);
    const hasTravelCost = (req?.travel_cost || 0) > 0;
    const hasOnSiteItems = (req?.items || []).length > 0 && ON_SITE_PRODUCT_IDS.size > 0;
    const canRecalcTravel = hasTravelCost || hasOnSiteItems;

    const save = async () => {
        if (!form.address_street.trim() || !form.address_city.trim() || !form.address_zip.trim()) {
            toast.error("Adres is verplicht (straat, postcode, gemeente)");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                address_street: form.address_street.trim(),
                address_city: form.address_city.trim(),
                address_zip: form.address_zip.trim(),
                email: form.email.trim() || null,
                phone: form.phone.trim(),
                property_link: form.property_link.trim(),
                description: form.description,
                publication_date: form.publication_date ? format(form.publication_date, "yyyy-MM-dd") : "",
                recalculate_travel: canRecalcTravel && recalcTravel,
            };
            const { data } = await api.patch(`/requests/${req.id}/details`, payload);
            toast.success("Gegevens bijgewerkt");
            onSaved?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
        setSaving(false);
    };

    if (!req) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#FFFFFF] border-zinc-200 text-zinc-950 max-w-2xl" data-testid="edit-details-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-[#E5A93D]" /> Aanvraag bewerken
                    </DialogTitle>
                    <p className="text-sm text-zinc-500">
                        Pas adres, contact en publicatiedatum aan. De makelaar krijgt een notificatie als het adres wijzigt.
                    </p>
                </DialogHeader>

                <div className="grid grid-cols-1 gap-4 py-2">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Straat + nr.</Label>
                        <AddressAutocomplete
                            value={form.address_street}
                            onChange={(v) => setForm((f) => ({ ...f, address_street: v }))}
                            onSelect={({ street, postcode, city }) => setForm((f) => ({
                                ...f,
                                address_street: street || f.address_street,
                                address_zip: postcode || f.address_zip,
                                address_city: city || f.address_city,
                            }))}
                            placeholder="Kerkstraat 12"
                            className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="edit-address-street"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-1">
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Postcode</Label>
                            <Input
                                value={form.address_zip}
                                onChange={(e) => setForm((f) => ({ ...f, address_zip: e.target.value }))}
                                placeholder="9000"
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                                data-testid="edit-address-zip"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Gemeente</Label>
                            <Input
                                value={form.address_city}
                                onChange={(e) => setForm((f) => ({ ...f, address_city: e.target.value }))}
                                placeholder="Gent"
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                                data-testid="edit-address-city"
                            />
                        </div>
                    </div>

                    {canRecalcTravel && (
                        <label className="flex items-start gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm cursor-pointer hover:border-[#E5A93D]/40 transition" data-testid="edit-recalc-travel-toggle">
                            <input
                                type="checkbox"
                                checked={recalcTravel}
                                onChange={(e) => setRecalcTravel(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#E5A93D]"
                                data-testid="edit-recalc-travel-checkbox"
                            />
                            <div>
                                <div className="text-sm font-medium text-zinc-950">Reiskost herberekenen</div>
                                <div className="text-xs text-zinc-500 mt-0.5">
                                    {hasTravelCost
                                        ? `Huidige reiskost: €${Number(req.travel_cost).toFixed(2)} — bij adreswijziging of bij aanvinken opnieuw berekend.`
                                        : "Reiskost staat momenteel op €0 — vink aan om opnieuw te berekenen via OpenStreetMap."}
                                </div>
                            </div>
                        </label>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                                data-testid="edit-email"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">GSM</Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                                data-testid="edit-phone"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Pand-link (optioneel)</Label>
                        <Input
                            value={form.property_link}
                            onChange={(e) => setForm((f) => ({ ...f, property_link: e.target.value }))}
                            placeholder="https://..."
                            className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="edit-property-link"
                        />
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Datum publicatie pand</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button className="w-full h-11 px-3 flex items-center gap-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-left text-sm hover:border-zinc-400" data-testid="edit-publication-date-trigger">
                                    <CalIcon className="h-4 w-4 text-zinc-500" />
                                    <span className={form.publication_date ? "text-zinc-950" : "text-zinc-500"}>
                                        {form.publication_date ? format(form.publication_date, "PPP", { locale: nl }) : "Geen publicatiedatum"}
                                    </span>
                                    {form.publication_date && (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); setForm((f) => ({ ...f, publication_date: null })); }}
                                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setForm((f) => ({ ...f, publication_date: null })); } }}
                                            className="ml-auto text-xs text-zinc-500 hover:text-red-600 cursor-pointer"
                                            data-testid="edit-publication-date-clear"
                                        >
                                            wissen
                                        </span>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-zinc-200">
                                <Calendar
                                    mode="single"
                                    selected={form.publication_date}
                                    onSelect={(d) => setForm((f) => ({ ...f, publication_date: d }))}
                                    locale={nl}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Beschrijving / info</Label>
                        <Textarea
                            rows={4}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="edit-description"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2 px-6 py-4 border-t border-zinc-100 bg-[#FFFFFF] flex-shrink-0">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                        className="text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
                        data-testid="edit-details-cancel"
                    >
                        Annuleren
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm"
                        data-testid="edit-details-save"
                    >
                        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opslaan…</> : "Opslaan"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
